import React, { useState } from 'react'
import styled from 'styled-components'
import { Button, AnimatedContentLoader, TableList, TextTooltip } from '@components'
import { useTranslation } from 'react-i18next'
import { useBusinessContext } from '@root/context'
import { fetchPISPaymentShortcuts, deletePISPaymentShortcut, IPISPaymentShortcut } from '@query'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FaTrashAlt } from 'react-icons/fa'
import toast from 'react-hot-toast'
import { SettingsSubPageLayout } from '@pages/SettingsPage/components/SettingsSubPageLayout'

export const PaymentsSettings: React.FC = () => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const queryClient = useQueryClient()
  const [deletingShortcutId, setDeletingShortcutId] = useState<number | null>(null)

  const {
    data: shortcuts,
    isLoading,
    isError
  } = useQuery<IPISPaymentShortcut[]>({
    queryKey: [slug, 'pis_shortcuts'],
    queryFn: () => fetchPISPaymentShortcuts({ slug }),
    enabled: Boolean(slug)
  })

  const deleteMutation = useMutation<void, unknown, { shortcutId: number }>({
    mutationFn: ({ shortcutId }) => deletePISPaymentShortcut({ slug, shortcutId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [slug, 'pis_shortcuts'] })
      toast.success(t('settings.payments.shortcuts.deleteSuccess'))
    },
    onError: () => {
      toast.error(t('settings.payments.shortcuts.deleteError'))
    },
    onSettled: () => {
      setDeletingShortcutId(null)
    }
  })

  const handleDelete = (shortcutId: number) => {
    if (!slug) {
      return
    }
    setDeletingShortcutId(shortcutId)
    deleteMutation.mutate({ shortcutId })
  }

  const hasSlug = Boolean(slug)
  const resolvedShortcuts = shortcuts ?? []

  return (
    <SettingsSubPageLayout title={t('settings.payments.title')}>
      <ContentWrapper>
        <Section>
          <SectionHeader>
            <SectionDescription>{t('settings.payments.shortcuts.description')}</SectionDescription>
          </SectionHeader>

          <AnimatedContentLoader
            isLoading={isLoading}
            isEmpty={hasSlug && !resolvedShortcuts.length}
            isEmptyDescription={t('settings.payments.shortcuts.empty')}
            isError={isError}
            errorDescription={t('settings.payments.shortcuts.loadError')}
          >
            <TableList<IPISPaymentShortcut>
              items={resolvedShortcuts}
              itemHeight={TableListItemHeight}
              renderItem={shortcut => {
                const placeholderLabel = shortcut.provider_name
                  ? shortcut.provider_name.slice(0, 2).toUpperCase()
                  : t('settings.payments.shortcuts.placeholder')

                return (
                  <>
                    <ShortcutProfile>
                      <LogoWrapper>
                        {shortcut.provider_logo_url ? (
                          <LogoImage
                            src={shortcut.provider_logo_url}
                            alt={
                              shortcut.provider_name ?? t('settings.payments.shortcuts.shortcut')
                            }
                            loading="lazy"
                          />
                        ) : (
                          <LogoPlaceholder>{placeholderLabel}</LogoPlaceholder>
                        )}
                      </LogoWrapper>
                      <ShortcutInfo>
                        <ShortcutName>
                          {shortcut.provider_name ?? t('settings.payments.shortcuts.shortcut')}
                        </ShortcutName>
                      </ShortcutInfo>
                    </ShortcutProfile>
                    <ShortcutActions>
                      <TextTooltip
                        tooltip={t('settings.payments.shortcuts.removeTooltip')}
                        placement="bottom-end"
                      >
                        <Button
                          icon={<FaTrashAlt />}
                          intent="danger"
                          isSecondary
                          size="sm"
                          onClick={() => handleDelete(shortcut.id)}
                          showSpinner={
                            deleteMutation.isPending && deletingShortcutId === shortcut.id
                          }
                          aria-label={t('settings.payments.shortcuts.remove')}
                        />
                      </TextTooltip>
                    </ShortcutActions>
                  </>
                )
              }}
            ></TableList>
          </AnimatedContentLoader>
        </Section>
      </ContentWrapper>
    </SettingsSubPageLayout>
  )
}

const ContentWrapper = styled.div`
  flex: 1;
`

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
`

const SectionDescription = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
`

const LogoWrapper = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.neutralGray};
  flex-shrink: 0;
  overflow: hidden;
`

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`

const LogoPlaceholder = styled.span`
  color: ${({ theme }) => theme.colors.neutralWhite};
  font-weight: 700;
`

const ShortcutProfile = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  flex: 1;
`

const ShortcutInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxxs}rem;
  min-width: 0;
`

const ShortcutName = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
`

const ShortcutActions = styled.div`
  flex-shrink: 0;
`

const TableListItemHeight = 64
